/**
 * Pure parser for geo: URIs and Google Maps URLs — no imports, no side effects,
 * so it can be unit-tested with plain `node --test` (no npm install). The
 * intent-handling / navigation / toast side lives in geoIntent.js, which
 * re-exports parseGeoUri so existing `from './geoIntent'` imports keep working.
 *
 * Extracts geographic coordinates, place labels, or query strings from standard
 * geo URIs and Maps URLs.
 */

/**
 * Parse any geo URI or Google Maps URL into coordinate / place metadata.
 * @param {string} rawUri
 * @returns {{ lat?: number, lng?: number, name?: string, query?: string, isShortLink?: boolean } | null}
 */
export const parseGeoUri = (rawUri) => {
  if (!rawUri || typeof rawUri !== 'string') return null;
  const uri = rawUri.trim();

  // 1. Check for geo: URI scheme (e.g. geo:13.6547,78.9445 or geo:0,0?q=13.6547,78.9445(Label))
  if (uri.startsWith('geo:')) {
    const withoutScheme = uri.slice(4);
    const [coordsPart, queryPart] = withoutScheme.split('?');

    // Case 1A: geo:0,0?q=...
    if (queryPart) {
      const qParams = new URLSearchParams(queryPart);
      const q = qParams.get('q') || '';
      if (q) {
        // Check for q=lat,lng(Label)
        const labelMatch = q.match(/^([+-]?\d+(?:\.\d+)?),([+-]?\d+(?:\.\d+)?)(?:\((.*)\))?$/);
        if (labelMatch) {
          const lat = parseFloat(labelMatch[1]);
          const lng = parseFloat(labelMatch[2]);
          const name = labelMatch[3] ? decodeURIComponent(labelMatch[3]).trim() : '';
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { lat, lng, name: name || undefined };
          }
        }
        // General text query e.g. q=Piler+Bus+Stand
        return { query: decodeURIComponent(q).trim() };
      }
    }

    // Case 1B: geo:lat,lng
    if (coordsPart && coordsPart !== '0,0') {
      const parts = coordsPart.split(',');
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0]);
        const lng = parseFloat(parts[1]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      }
    }
  }

  // 2. Check for Google Maps URLs (http / https)
  if (uri.includes('google.com/maps') || uri.includes('maps.google.com') || uri.includes('maps.app.goo.gl') || uri.includes('goo.gl/maps')) {
    // Shortened links
    if (uri.includes('maps.app.goo.gl') || uri.includes('goo.gl/maps')) {
      return { isShortLink: true, rawUrl: uri };
    }

    try {
      const parsedUrl = new URL(uri);
      const params = parsedUrl.searchParams;

      // Check query params: q, query, destination, daddr, ll
      const candidateParam = params.get('q') || params.get('query') || params.get('destination') || params.get('daddr') || params.get('ll') || '';
      if (candidateParam) {
        const match = candidateParam.match(/^([+-]?\d+(?:\.\d+)?),([+-]?\d+(?:\.\d+)?)(?:\((.*)\))?$/);
        if (match) {
          const lat = parseFloat(match[1]);
          const lng = parseFloat(match[2]);
          const name = match[3] ? decodeURIComponent(match[3]).trim() : '';
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { lat, lng, name: name || undefined };
          }
        }
        return { query: decodeURIComponent(candidateParam).trim() };
      }

      // Check URL path for @lat,lng e.g. /maps/@13.6547,78.9445,17z or /maps/place/.../@13.6547,78.9445
      const atMatch = parsedUrl.pathname.match(/@([+-]?\d+(?:\.\d+)?),([+-]?\d+(?:\.\d+)?)/);
      if (atMatch) {
        const lat = parseFloat(atMatch[1]);
        const lng = parseFloat(atMatch[2]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          // Check if place name is in path: /place/Place+Name/@lat,lng
          const placeMatch = parsedUrl.pathname.match(/\/place\/([^/@]+)/);
          const name = placeMatch ? decodeURIComponent(placeMatch[1].replace(/\+/g, ' ')).trim() : '';
          return { lat, lng, name: name || undefined };
        }
      }
    } catch {
      // Fallback regex if URL parsing fails on custom strings
      const fallbackMatch = uri.match(/([+-]?\d{1,2}\.\d{4,}),([+-]?\d{1,3}\.\d{4,})/);
      if (fallbackMatch) {
        const lat = parseFloat(fallbackMatch[1]);
        const lng = parseFloat(fallbackMatch[2]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      }
    }
  }

  // 3. Raw "lat,lng" string fallback
  const rawCoordsMatch = uri.match(/^([+-]?\d{1,2}\.\d{4,})\s*,\s*([+-]?\d{1,3}\.\d{4,})$/);
  if (rawCoordsMatch) {
    const lat = parseFloat(rawCoordsMatch[1]);
    const lng = parseFloat(rawCoordsMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
  }

  return null;
};
